import { useEffect } from "react";
import Icon from "@/components/Icon";
import ListItem from "@/components/ListItem/ListItem";
import TextField from "@/components/TextField";
import {
  useAlgoliaSearch,
  AlgoliaSearchResults,
  type PersonHit,
  createPersonOnlySearchUiState,
  getPersonDisplayName,
  getPersonSearchHitSupportingText,
  isPersonExactMatch,
  createPersonOnlyHitRenderer
} from "@/components/AlgoliaSearch";

const personSearchIndexName = import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME;

interface GovernanceRolePersonPickerProps {
  selectedPerson: PersonHit | null;
  onSelect: (hit: PersonHit) => void;
  onClear: () => void;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  label?: string;
  showContactDetails?: boolean;
}

export function GovernanceRolePersonPicker({
  selectedPerson,
  onSelect,
  onClear,
  error,
  errorText,
  disabled,
  label = "Person",
  showContactDetails
}: GovernanceRolePersonPickerProps) {
  const {
    query: personQuery,
    displayValue,
    results: personResults,
    isLoading: personLoading,
    showResults,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear: handleAlgoliaClear,
    handleItemSelected,
    setShowResults,
    setIsFocused,
    setDisplayValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useAlgoliaSearch<PersonHit>({
    indexName: personSearchIndexName,
    hitsPerPage: 7,
    initialUiState: createPersonOnlySearchUiState(personSearchIndexName),
    getDisplayValue: getPersonDisplayName,
    isExactMatch: isPersonExactMatch,
    onSelect
  });

  const hasActivePersonQuery = personQuery.trim() !== "";

  useEffect(() => {
    if (!selectedPerson) {
      focusInput();
    }
  }, [selectedPerson, focusInput]);

  useEffect(() => {
    if (displayValue) {
      setDisplayValue(undefined);
    }
  }, [displayValue, setDisplayValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchBoxRef.current &&
        !searchBoxRef.current.contains(event.target as Node) &&
        resultsContainerRef.current &&
        !resultsContainerRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchBoxRef, resultsContainerRef, setShowResults]);

  useEffect(() => {
    if (!showResults) return;

    const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setShowResults(false);
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [showResults, setShowResults]);

  const handleClearPerson = () => {
    handleAlgoliaClear();
    setDisplayValue(undefined);
    onClear();
    requestAnimationFrame(() => focusInput());
  };

  const personTrailingIcon =
    displayValue || personQuery ? (
      <button
        type="button"
        className="cursor-pointer flex items-center h-full"
        onClick={handleClearPerson}
        aria-label="Clear search query"
        disabled={disabled}
      >
        <Icon name="cancel" />
      </button>
    ) : undefined;

  const selectedPersonSupportingText = selectedPerson
    ? getPersonSearchHitSupportingText(selectedPerson, { showContactDetails })
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      {!selectedPerson && (
        <div className="relative">
          <div ref={searchBoxRef}>
            <TextField
              label={label}
              placeholder="Search by name, PGA ID, or email"
              value={displayValue || personQuery}
              onChangeText={(value) => {
                setDisplayValue(undefined);
                handleQueryChange(value);
              }}
              onFocus={() => {
                setIsFocused(true);
                if (personQuery.trim() !== "") {
                  setShowResults(true);
                }
              }}
              onBlur={() => setIsFocused(false)}
              leadingIcon="search"
              trailingIcon={personTrailingIcon}
              variant="outlined"
              className="w-full"
              required
              error={error}
              errorText={errorText}
              disabled={disabled}
            />
          </div>

          {showResults && hasActivePersonQuery && (
            <AlgoliaSearchResults
              results={personResults}
              query={personQuery}
              isLoading={personLoading}
              highlightedIndex={highlightedIndex}
              setHighlightedIndex={setHighlightedIndex}
              handleItemSelected={handleItemSelected}
              itemRefs={itemRefs}
              resultsContainerRef={resultsContainerRef}
              renderHit={createPersonOnlyHitRenderer({ showContactDetails })}
              isExactMatch={isPersonExactMatch}
              className="absolute z-50 mt-1 w-full bg-sys-brand-surface shadow-md rounded-md p-3 max-h-80 overflow-y-auto"
            />
          )}
        </div>
      )}

      {selectedPerson && (
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <ListItem
              compact
              useAvatar
              picture={selectedPerson.profile_photo}
              headline={getPersonDisplayName(selectedPerson)}
              supportingText={selectedPersonSupportingText}
              longContent={showContactDetails || !!selectedPersonSupportingText}
            />
          </div>
          <button
            type="button"
            className="cursor-pointer flex items-center shrink-0 p-1 rounded-full hover:bg-sys-brand-surface-container-high transition-colors"
            onClick={handleClearPerson}
            aria-label="Remove selected person"
            disabled={disabled}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
      )}
    </div>
  );
}
